import { supabase } from '@/lib/supabase';
import type { Profile } from '@/types';

/** Minimal profile slice for rendering a pin's author / source chat. */
export type PinProfile = Pick<Profile, 'id' | 'display_name' | 'avatar_url' | 'handle'>;

/** A pinned message, enriched for display in the inbox rail + full page. */
export interface PinnedMessage {
  pinId: string;
  messageId: string;
  conversationId: string;
  body: string | null;
  /** Signed/absolute URL for an image attachment, if any. */
  mediaUrl: string | null;
  /** First URL found in the message body, if any. */
  link: string | null;
  /** When the original message was sent. */
  createdAt: string;
  /** When the user pinned it. */
  pinnedAt: string;
  /** Who sent the message. */
  author: PinProfile | null;
  /** The other party in the DM — i.e. which chat this came from. */
  peer: PinProfile | null;
}

const MEDIA_BUCKET = 'message-media';
const SIGNED_URL_TTL = 60 * 60;
const UUID_RE = /^[0-9a-f-]{36}$/i;
const URL_RE = /\bhttps?:\/\/[^\s<>"')]+/i;

function firstLink(body: string | null): string | null {
  if (!body) return null;
  return body.match(URL_RE)?.[0] ?? null;
}

function isExternalMedia(media: string): boolean {
  return /^https?:\/\//i.test(media);
}

async function fetchProfiles(ids: string[]): Promise<Map<string, PinProfile>> {
  const map = new Map<string, PinProfile>();
  if (!ids.length) return map;
  const { data, error } = await supabase
    .from('profiles')
    .select('id, display_name, avatar_url, handle')
    .in('id', ids);
  if (error) throw error;
  for (const p of data ?? []) map.set(p.id, p);
  return map;
}

async function resolveMediaUrls(medias: string[]): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  const paths = medias.filter((m) => !isExternalMedia(m));
  for (const m of medias) if (isExternalMedia(m)) out.set(m, m);
  if (paths.length) {
    const { data } = await supabase.storage
      .from(MEDIA_BUCKET)
      .createSignedUrls([...new Set(paths)], SIGNED_URL_TTL);
    for (const item of data ?? []) {
      if (item.path && item.signedUrl) out.set(item.path, item.signedUrl);
    }
  }
  return out;
}

/** Pin a message for the current user. Idempotent — re-pinning is a no-op. */
export async function pinMessage(userId: string, messageId: string): Promise<void> {
  if (!UUID_RE.test(messageId)) return;
  const { error } = await supabase.from('message_pins').insert({ user_id: userId, message_id: messageId });
  if (error && error.code !== '23505') throw error;
}

/** Remove a pin. */
export async function unpinMessage(userId: string, messageId: string): Promise<void> {
  if (!UUID_RE.test(messageId)) return;
  const { error } = await supabase
    .from('message_pins')
    .delete()
    .eq('user_id', userId)
    .eq('message_id', messageId);
  if (error) throw error;
}

/** Which of the given message ids the user has pinned (drives the chat toggle state). */
export async function listPinnedIdsForMessages(userId: string, messageIds: string[]): Promise<string[]> {
  if (!messageIds.length) return [];
  const { data, error } = await supabase
    .from('message_pins')
    .select('message_id')
    .eq('user_id', userId)
    .in('message_id', messageIds);
  if (error) throw error;
  return (data ?? []).map((r) => r.message_id);
}

/**
 * Every message the user has pinned, newest first, enriched with the author,
 * the source chat (DM peer), a resolved image URL, and the first link in the
 * body. Deleted messages are dropped.
 */
export async function listMyPins(userId: string): Promise<PinnedMessage[]> {
  const { data: pins, error } = await supabase
    .from('message_pins')
    .select('id, message_id, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  const pinRows = pins ?? [];
  if (!pinRows.length) return [];

  const messageIds = pinRows.map((p) => p.message_id);
  const { data: messages, error: msgError } = await supabase
    .from('messages')
    .select('id, body, sender_id, conversation_id, created_at')
    .in('id', messageIds)
    .is('deleted_at', null);
  if (msgError) throw msgError;
  const msgById = new Map((messages ?? []).map((m) => [m.id, m] as const));

  const liveIds = [...msgById.keys()];
  const conversationIds = [...new Set((messages ?? []).map((m) => m.conversation_id))];

  const [{ data: attachments }, { data: memberRows }] = await Promise.all([
    liveIds.length
      ? supabase
          .from('message_attachments')
          .select('message_id, storage_path')
          .in('message_id', liveIds)
          .eq('kind', 'image')
      : Promise.resolve({ data: [] as { message_id: string; storage_path: string }[] }),
    conversationIds.length
      ? supabase
          .from('conversation_members')
          .select('conversation_id, user_id')
          .in('conversation_id', conversationIds)
      : Promise.resolve({ data: [] as { conversation_id: string; user_id: string }[] }),
  ]);

  // Source chat = the other member of the DM.
  const peerByConversation = new Map<string, string>();
  for (const row of memberRows ?? []) {
    if (row.user_id !== userId) peerByConversation.set(row.conversation_id, row.user_id);
  }

  const mediaByMessage = new Map((attachments ?? []).map((a) => [a.message_id, a.storage_path] as const));
  const [mediaUrls, profiles] = await Promise.all([
    resolveMediaUrls([...mediaByMessage.values()]),
    fetchProfiles([
      ...new Set([
        ...(messages ?? []).map((m) => m.sender_id),
        ...peerByConversation.values(),
      ]),
    ]),
  ]);

  return pinRows
    .map((pin): PinnedMessage | null => {
      const m = msgById.get(pin.message_id);
      if (!m) return null; // message was deleted
      const media = mediaByMessage.get(m.id) ?? null;
      const peerId = peerByConversation.get(m.conversation_id) ?? null;
      return {
        pinId: pin.id,
        messageId: m.id,
        conversationId: m.conversation_id,
        body: m.body,
        mediaUrl: media ? (mediaUrls.get(media) ?? null) : null,
        link: firstLink(m.body),
        createdAt: m.created_at,
        pinnedAt: pin.created_at,
        author: profiles.get(m.sender_id) ?? null,
        peer: peerId ? (profiles.get(peerId) ?? null) : null,
      };
    })
    .filter((p): p is PinnedMessage => p !== null);
}
